import { useState, useCallback, useEffect } from 'react';
import { FingerType, FingerPosition, HandType } from '@/types';
import { DEFAULT_FINGER_POSITIONS } from '@/data/fingerKeyMapping';

export interface FingerPositioningState {
  positions: Record<FingerType, FingerPosition>;
  isCalibrating: boolean;
  activeFingers: {
    current: FingerType | null;
    next: FingerType | null;
  };
  handDimensions: {
    left: { width: number; height: number };
    right: { width: number; height: number };
  };
}

const STORAGE_KEY = 'keyboard-hero-finger-positions';

const createDefaultPositions = (): Record<FingerType, FingerPosition> => {
  const positions: Record<FingerType, FingerPosition> = {} as Record<FingerType, FingerPosition>;
  
  // Left hand positions
  Object.entries(DEFAULT_FINGER_POSITIONS.left).forEach(([finger, pos]) => {
    positions[finger as FingerType] = {
      x: pos.x,
      y: pos.y,
      fingerType: finger as FingerType
    };
  });
  
  // Right hand positions
  Object.entries(DEFAULT_FINGER_POSITIONS.right).forEach(([finger, pos]) => {
    positions[finger as FingerType] = {
      x: pos.x,
      y: pos.y,
      fingerType: finger as FingerType
    };
  });
  
  return positions;
};

export const useFingerPositioning = () => {
  const [state, setState] = useState<FingerPositioningState>({
    positions: createDefaultPositions(),
    isCalibrating: false,
    activeFingers: { current: null, next: null },
    handDimensions: {
      left: { width: 200, height: 300 },
      right: { width: 200, height: 300 }
    }
  });

  /**
   * Load saved positions from localStorage on mount
   */
  useEffect(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const savedPositions = JSON.parse(saved);
        setState(prev => ({
          ...prev,
          positions: { ...prev.positions, ...savedPositions }
        }));
      }
    } catch (error) {
      console.warn('Failed to load saved finger positions:', error);
    }
  }, []);

  /**
   * Save positions to localStorage
   */
  const savePositions = useCallback((positions: Record<FingerType, FingerPosition>) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(positions));
    } catch (error) {
      console.warn('Failed to save finger positions:', error);
    }
  }, []);

  /**
   * Updates position for a specific finger
   */
  const updateFingerPosition = useCallback((
    fingerType: FingerType, 
    x: number, 
    y: number
  ) => {
    setState(prev => {
      const newPositions = {
        ...prev.positions,
        [fingerType]: { x, y, fingerType }
      };
      
      // Save to localStorage
      savePositions(newPositions);
      
      return {
        ...prev,
        positions: newPositions
      };
    });
  }, [savePositions]);

  /**
   * Updates multiple finger positions at once
   */
  const updateMultiplePositions = useCallback((
    updates: Partial<Record<FingerType, { x: number; y: number }>>
  ) => {
    setState(prev => {
      const newPositions = { ...prev.positions };
      
      Object.entries(updates).forEach(([finger, pos]) => {
        if (pos) {
          newPositions[finger as FingerType] = {
            x: pos.x,
            y: pos.y,
            fingerType: finger as FingerType
          };
        }
      });
      
      // Save to localStorage
      savePositions(newPositions);
      
      return {
        ...prev,
        positions: newPositions
      };
    });
  }, [savePositions]);

  /**
   * Sets active fingers (current and next)
   */
  const setActiveFingers = useCallback((
    current: FingerType | null, 
    next: FingerType | null
  ) => {
    setState(prev => ({
      ...prev,
      activeFingers: { current, next }
    }));
  }, []);

  /**
   * Starts calibration mode
   */
  const startCalibration = useCallback(() => {
    setState(prev => ({ ...prev, isCalibrating: true }));
  }, []);

  /**
   * Ends calibration mode
   */
  const endCalibration = useCallback(() => {
    setState(prev => ({ ...prev, isCalibrating: false }));
  }, []);

  /**
   * Resets positions to defaults
   */
  const resetToDefaults = useCallback(() => {
    const defaultPositions = createDefaultPositions();
    setState(prev => ({
      ...prev,
      positions: defaultPositions
    }));
    savePositions(defaultPositions);
  }, [savePositions]);

  /**
   * Updates hand dimensions (when hand images are loaded)
   */
  const updateHandDimensions = useCallback((
    hand: HandType, 
    width: number, 
    height: number
  ) => {
    setState(prev => ({
      ...prev,
      handDimensions: {
        ...prev.handDimensions,
        [hand]: { width, height }
      }
    }));
  }, []);

  /**
   * Gets position for a specific finger
   */
  const getFingerPosition = useCallback((fingerType: FingerType): FingerPosition => {
    return state.positions[fingerType];
  }, [state.positions]);

  /**
   * Gets all positions for a specific hand
   */
  const getHandPositions = useCallback((hand: HandType): FingerPosition[] => {
    const handFingers = hand === 'left' 
      ? ['leftPinky', 'leftRing', 'leftMiddle', 'leftPointing', 'leftThumb'] as FingerType[]
      : ['rightThumb', 'rightPointing', 'rightMiddle', 'rightRing', 'rightPinky'] as FingerType[];
    
    return handFingers.map(finger => state.positions[finger]);
  }, [state.positions]);

  /**
   * Converts relative position to absolute pixels
   */
  const getAbsolutePosition = useCallback((
    fingerType: FingerType, 
    containerWidth: number, 
    containerHeight: number
  ): { x: number; y: number } => {
    const position = state.positions[fingerType];
    return {
      x: position.x * containerWidth,
      y: position.y * containerHeight
    };
  }, [state.positions]);

  /**
   * Converts absolute pixels to relative position
   */
  const getRelativePosition = useCallback((
    absoluteX: number, 
    absoluteY: number, 
    containerWidth: number, 
    containerHeight: number
  ): { x: number; y: number } => {
    return {
      x: absoluteX / containerWidth,
      y: absoluteY / containerHeight
    };
  }, []);

  /**
   * Validates if a position is within bounds
   */
  const isValidPosition = useCallback((x: number, y: number): boolean => {
    return x >= 0 && x <= 1 && y >= 0 && y <= 1;
  }, []);

  /**
   * Gets finger circle style for rendering
   */
  const getFingerCircleStyle = useCallback((
    fingerType: FingerType,
    containerWidth: number,
    containerHeight: number,
    circleSize: number = 20
  ): React.CSSProperties => {
    const position = getAbsolutePosition(fingerType, containerWidth, containerHeight);
    const { current, next } = state.activeFingers;
    
    let backgroundColor = '#374151'; // Default gray
    let borderColor = '#6b7280';
    
    if (current === fingerType) {
      backgroundColor = '#22c55e'; // Green for current
      borderColor = '#16a34a';
    } else if (next === fingerType) {
      backgroundColor = '#3b82f6'; // Blue for next
      borderColor = '#2563eb';
    }
    
    return {
      position: 'absolute',
      left: position.x - circleSize / 2,
      top: position.y - circleSize / 2,
      width: circleSize,
      height: circleSize,
      borderRadius: '50%',
      backgroundColor,
      border: `2px solid ${borderColor}`,
      cursor: state.isCalibrating ? 'move' : 'default',
      transition: 'all 0.2s ease-out',
      transform: state.isCalibrating ? 'scale(1.2)' : 'scale(1)',
      zIndex: 10
    };
  }, [state.activeFingers, state.isCalibrating, getAbsolutePosition]);

  /**
   * Exports current configuration
   */
  const exportConfiguration = useCallback(() => {
    return {
      positions: state.positions,
      handDimensions: state.handDimensions,
      timestamp: Date.now()
    };
  }, [state.positions, state.handDimensions]);

  /**
   * Imports configuration
   */
  const importConfiguration = useCallback((config: any) => {
    try {
      if (config.positions) {
        setState(prev => ({
          ...prev,
          positions: config.positions,
          handDimensions: config.handDimensions || prev.handDimensions
        }));
        savePositions(config.positions);
      }
    } catch (error) {
      console.error('Failed to import configuration:', error);
    }
  }, [savePositions]);

  return {
    // State
    positions: state.positions,
    isCalibrating: state.isCalibrating,
    activeFingers: state.activeFingers,
    handDimensions: state.handDimensions,
    
    // Actions
    updateFingerPosition,
    updateMultiplePositions,
    setActiveFingers,
    startCalibration,
    endCalibration,
    resetToDefaults,
    updateHandDimensions,
    
    // Getters
    getFingerPosition,
    getHandPositions,
    getAbsolutePosition,
    getRelativePosition,
    getFingerCircleStyle,
    
    // Utilities
    isValidPosition,
    exportConfiguration,
    importConfiguration
  };
};

/**
 * Hook for managing finger hint animations
 */
export const useFingerHintAnimations = () => {
  const [animationStates, setAnimationStates] = useState<
    Record<FingerType, 'idle' | 'active' | 'next' | 'transitioning'>
  >({} as Record<FingerType, 'idle' | 'active' | 'next' | 'transitioning'>);

  const setFingerState = useCallback((
    fingerType: FingerType, 
    state: 'idle' | 'active' | 'next' | 'transitioning'
  ) => {
    setAnimationStates(prev => ({
      ...prev,
      [fingerType]: state
    }));
  }, []);

  const transitionFingers = useCallback((
    _from: FingerType | null, 
    to: FingerType | null
  ) => {
    setAnimationStates(prev => {
      const newStates = { ...prev };
      
      // Reset all to idle
      Object.keys(newStates).forEach(finger => {
        newStates[finger as FingerType] = 'idle';
      });
      
      // Set new active finger
      if (to) {
        newStates[to] = 'active';
      }
      
      return newStates;
    });
  }, []);

  return {
    animationStates,
    setFingerState,
    transitionFingers
  };
};
